/**
 * CommentBoxNode Component
 *
 * A comment box rendered as a React Flow node (behind beat nodes)
 * Similar to comment boxes in Houdini or UE Blueprints
 */

import { CSSProperties, useState, useRef, useEffect, memo } from 'react';
import { NodeProps, NodeResizeControl } from '@xyflow/react';
import type { CommentBoxData } from '@/types';
import { useProjectStore } from '@/stores/projectStore';

function CommentBoxNode({ id, data, selected }: NodeProps) {
  if (!data) {
    console.error('[CommentBoxNode] No data provided for node:', id);
    return null;
  }
  
  const commentData = data as CommentBoxData;
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(commentData?.title || 'Comment');
  const inputRef = useRef<HTMLInputElement>(null);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const [hasStartedResize, setHasStartedResize] = useState(false);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle title edit
  const handleTitleSubmit = () => {
    if (editedTitle.trim() && editedTitle !== commentData?.title && commentData?.onUpdate) {
      commentData.onUpdate(id, { data: { title: editedTitle.trim() } });
    }
    setIsEditing(false);
  };

  // Handle title double click
  const handleTitleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    position: 'relative',
    pointerEvents: 'none', // Allow panning through everything by default
  };

  const borderBoxStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    border: `2px solid ${commentData?.color || '#E47019'}`,
    backgroundColor: `${commentData?.color || '#E47019'}15`, // 15 = ~8% opacity in hex
    borderRadius: 'var(--nb-radius-md)',
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none', // Visual only, no interaction
  };

  const headerStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 30,
    backgroundColor: commentData?.color || '#E47019',
    borderRadius: 'var(--nb-radius-md) var(--nb-radius-md) 0 0',
    display: 'flex',
    alignItems: 'center',
    padding: '0 var(--nb-space-sm)',
    gap: 'var(--nb-space-xs)',
    userSelect: 'none',
    cursor: 'move', // Indicate draggable
    pointerEvents: 'all', // Enable interaction with header
  };

  const titleStyles: CSSProperties = {
    flex: 1,
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 600,
    color: 'white',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const inputStyles: CSSProperties = {
    flex: 1,
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 600,
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '2px var(--nb-space-xs)',
    outline: 'none',
  };

  const deleteButtonStyles: CSSProperties = {
    width: 20,
    height: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    border: 'none',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const handleResizeStart = () => {
    if (!hasStartedResize) {
      saveHistory();
      setHasStartedResize(true);
    }
  };

  const handleResizeEnd = () => {
    setHasStartedResize(false);
  };

  const controlStyle: CSSProperties = {
    background: commentData?.color || '#E47019',
    width: 8,
    height: 8,
    borderRadius: 2,
    pointerEvents: 'all', // Enable interaction with resize handles
  };

  return (
    <>
      {/* Resize controls - only show when selected */}
      {selected && (
        <>
          <NodeResizeControl 
            style={controlStyle} 
            position="top-left"
            onResizeStart={handleResizeStart}
            onResizeEnd={handleResizeEnd}
          />
          <NodeResizeControl 
            style={controlStyle} 
            position="top-right"
            onResizeStart={handleResizeStart}
            onResizeEnd={handleResizeEnd}
          />
          <NodeResizeControl 
            style={controlStyle} 
            position="bottom-left"
            onResizeStart={handleResizeStart}
            onResizeEnd={handleResizeEnd}
          />
          <NodeResizeControl 
            style={controlStyle} 
            position="bottom-right"
            onResizeStart={handleResizeStart}
            onResizeEnd={handleResizeEnd}
          />
        </>
      )}
      
      {/* Container - allows panning */}
      <div style={containerStyles}>
        {/* Visual border and background - no interaction */}
        <div style={borderBoxStyles} />
        
        {/* Header - drag handle */}
        <div className="comment-box-drag-handle" style={headerStyles}>
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              style={inputStyles}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSubmit();
                if (e.key === 'Escape') {
                  setEditedTitle(commentData?.title || 'Comment');
                  setIsEditing(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div style={titleStyles} onDoubleClick={handleTitleDoubleClick}>
              {commentData?.title || 'Comment'}
            </div>
          )}
          
          <button
            style={deleteButtonStyles}
            onClick={(e) => {
              e.stopPropagation();
              if (commentData?.onDelete) {
                commentData.onDelete(id);
              }
            }}
            title="Delete comment box"
          >
            ×
          </button>
        </div>
      </div>
    </>
  );
}

export default memo(CommentBoxNode);
